import { shortDate, type InviteResult } from '../lib/api'
import { answerWords, canStillAnswer, mailWent, retriesByItself, switchMailOn, whyNotEmailed } from '../lib/mail'
import { TASK_VERB } from '../lib/status'
import { Icon } from './ui'

// What Send questionnaire answers, once it has answered.
//
// Three outcomes, and only one of them is "Sent". The API issues the
// assessment and queues the invitation whatever happens to mail, and then
// says whether the worker will hand it to a carrier ("mail": QUEUED). On an
// installation whose mail is muted, or a workspace that switched mail off,
// nothing is emailed: the vendor gets neither the invitation nor, usually, the
// one time code they need to sign in. That screen used to say "Sent" and "On
// its way to" anyway. It now says what is true and what to do, including when
// the vendor can still sign in with a code by SMS or WhatsApp ("reach").

export function InviteSent({ sent, vendorName, vendorId }: { sent: InviteResult; vendorName: string; vendorId: string }) {
  const issued = sent.status === 'ISSUED'
  const went = issued && mailWent(sent)
  const heading = !issued ? 'Waiting for a second person' : went ? 'Sent' : 'Recorded, not emailed'
  const to = sent.invitedEmail || 'the vendor'

  return (
    <div className="p1-page p1-wizard">
      <div className="p1-head">
        <div>
          <h1>{heading}</h1>
        </div>
      </div>
      <section className="panel p1-sent">
        {!issued && (
          <>
            <h2>Drafted, not sent</h2>
            <p>{sent.note}</p>
          </>
        )}

        {went && (
          <>
            <div className="sent-hero">
              <span className="check-pop">
                <Icon name="check" size={26} />
              </span>
              <h2 style={{ margin: 0 }}>On its way to {sent.invitedEmail}</h2>
            </div>
            <p>
              {vendorName} has until {shortDate(sent.assessment?.dueOn)} to answer. You will see it on the vendor and in My work the moment their answers come
              back.
            </p>
            <dl className="kv">
              <dt>Reference</dt>
              <dd className="mono">{sent.assessment?.reference}</dd>
              <dt>Link in the email</dt>
              <dd className="mono">{sent.assessment?.link}</dd>
            </dl>
          </>
        )}

        {issued && !went && (
          <>
            <div className="note warn" role="status">
              <strong>Nothing was emailed to {to}</strong>
              <p style={{ margin: '4px 0 0' }}>{whyNotEmailed(sent.mail)}</p>
            </div>
            <p>{answerWords(sent, vendorName)}</p>
            <h2>What to do</h2>
            <ol>
              {canStillAnswer(sent) && <li>To let them start now, give them the link below; the portal sends the code to their phone.</li>}
              <li>{switchMailOn(sent)}</li>
              {retriesByItself(sent.mail) ? (
                <li>
                  If {to} still has nothing after a day, open the vendor and press {TASK_VERB.chase} on its next step.
                </li>
              ) : (
                <li>
                  Then open the vendor and press {TASK_VERB.chase} on its next step. The invitation goes to {to} with the same link and
                  due date.
                </li>
              )}
            </ol>
            <dl className="kv">
              <dt>Reference</dt>
              <dd className="mono">{sent.assessment?.reference}</dd>
              <dt>Answer by</dt>
              <dd>{shortDate(sent.assessment?.dueOn)}</dd>
              <dt>Link the invitation carries</dt>
              <dd className="mono">{sent.assessment?.link}</dd>
            </dl>
          </>
        )}

        {/* Back to the vendor, never Home: Home would show this questionnaire
            again in its own words (release 3.2, removal item 6). */}
        <div className="p1-actions" style={{ marginTop: 12 }}>
          <a className="button primary" href={'#/vendor/' + vendorId}>
            Back to the vendor
          </a>
          <a className="button" href="#/vendor/new">
            Add another vendor
          </a>
        </div>
      </section>
    </div>
  )
}
